import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, getApiErrorMessage } from '../lib/api';
import { resolveLocale } from '../lib/dateFormat';
import { useAuthStore, useUIStore } from '../store/index';
import { conversationKey, useChatReadStore } from '../store/chatRead';
import { Task, TaskMessage, User, WorkspaceGroup } from '../types';
import AuthenticatedImage from '../components/AuthenticatedImage';
import { useConfirm } from '../components/ConfirmDialogProvider';
import { getSocket } from '../lib/socket';

const TaskDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { language } = useUIStore();
  const confirm = useConfirm();
  const currentUser = useAuthStore((s) => s.user);
  const currentUserId = currentUser?.id;
  const markChatRead = useChatReadStore((s) => s.markRead);
  const [searchParams] = useSearchParams();
  const focus = searchParams.get('focus');

  const [task, setTask] = useState<Task | null>(null);
  const [members, setMembers] = useState<User[]>([]);
  const [groups, setGroups] = useState<WorkspaceGroup[]>([]);
  const [mentionableMembers, setMentionableMembers] = useState<
    Array<{ id: string; name: string; email: string }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  // Action states
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Save-as-template states
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [templateSuccess, setTemplateSuccess] = useState(false);

  // Task edit (title/description) state — mirrors the incident edit pattern
  // in IncidentDetails.tsx (isEditing flag, editError, save/cancel handlers).
  const [isEditingTask, setIsEditingTask] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Complete-with-note state (backend requires body {note} min 2 chars —
  // completeTaskSchema). UI must collect it before calling the API.
  const [showCompleteForm, setShowCompleteForm] = useState(false);
  const [completeNote, setCompleteNote] = useState('');

  // Note states
  const [noteText, setNoteText] = useState('');
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);

  // Watcher states
  const [watcherType, setWatcherType] = useState<'user' | 'group'>('user');
  const [watcherId, setWatcherId] = useState('');
  const [watcherLoading, setWatcherLoading] = useState(false);
  const [watcherError, setWatcherError] = useState<string | null>(null);
  const [watcherRemovingId, setWatcherRemovingId] = useState<string | null>(null);

  // Message edit/delete state — mirrors the incident comment pattern
  // (editingMessageId/editingMessageText) in IncidentDetails.tsx.
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingMessageText, setEditingMessageText] = useState('');
  const [messageActionLoading, setMessageActionLoading] = useState<string | null>(null);

  // Focus highlight (from URL ?focus=...)
  const [highlightedAction, setHighlightedAction] = useState<string | null>(null);

  // Reassign states
  const [showReassign, setShowReassign] = useState(false);
  const [reassignType, setReassignType] = useState<'user' | 'group'>('user');
  const [reassignId, setReassignId] = useState('');
  const [reassignNote, setReassignNote] = useState('');

  // Chat states
  const [messages, setMessages] = useState<TaskMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageError, setMessageError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  // @mention autocomplete — mentionQuery === null means the dropdown is
  // closed; mentionStart/mentionEnd are the text-index span (from the
  // triggering '@' to the current caret) that insertMention() replaces.
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionStart, setMentionStart] = useState(-1);
  const [mentionEnd, setMentionEnd] = useState(-1);
  const [mentionIndex, setMentionIndex] = useState(0);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const claimButtonRef = useRef<HTMLButtonElement>(null);
  const completeButtonRef = useRef<HTMLButtonElement>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotFound(false);
    setForbidden(false);
    try {
      const [taskRes, membersRes, groupsRes, mentionableRes] = await Promise.all([
        api.get(`/tasks/${id}`),
        api.get('/workspace/members'),
        api.get('/workspace/groups'),
        api.get(`/tasks/${id}/mentionable-users`)
      ]);
      const taskData = (taskRes.data as any)?.data;
      if (!taskData) {
        setNotFound(true);
        return;
      }
      setTask(taskData);

      const mData = (membersRes.data as any)?.data;
      if (Array.isArray(mData)) setMembers(mData);

      const gData = (groupsRes.data as any)?.data;
      if (Array.isArray(gData)) setGroups(gData);

      const mentionableData = (mentionableRes.data as any)?.data;
      if (Array.isArray(mentionableData)) setMentionableMembers(mentionableData);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setNotFound(true);
      } else if (err?.response?.status === 403) {
        setForbidden(true);
      } else {
        setError(getApiErrorMessage(err, 'Failed to load task.'));
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchData();
    } else {
      setNotFound(true);
    }
  }, [id, fetchData]);

  const fetchMessages = useCallback(async () => {
    setMessagesLoading(true);
    try {
      const res = await api.get(`/tasks/${id}/messages`);
      const data = (res.data as any)?.data;
      if (Array.isArray(data)) {
        setMessages(data);
      }
    } catch {
      // non-critical
    } finally {
      setMessagesLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id && task) {
      fetchMessages();
    }
  }, [id, task, fetchMessages]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Faz 2 (mobile-app plan): joins this task's socket room while the
  // page is open, live-appends messages from OTHER users/tabs. The
  // sender's own message already lands via fetchMessages() after their
  // own POST — de-dupe by id so the socket echo of a message we already
  // have doesn't render twice.
  useEffect(() => {
    if (!id) return;
    const socket = getSocket();
    if (!socket) return;

    socket.emit('join_task', id);

    const handleMessageAdded = (payload: { message: TaskMessage; entityId: string; authorId: string }) => {
      if (payload.entityId !== id) return;
      setMessages((prev) => {
        if (prev.some((m) => m.id === payload.message.id)) return prev;
        return [...prev, payload.message];
      });
    };

    socket.on('task_message_added', handleMessageAdded);

    return () => {
      socket.emit('leave_task', id);
      socket.off('task_message_added', handleMessageAdded);
    };
  }, [id]);

  // Opening a task counts as reading its message thread (chat badge).
  useEffect(() => {
    if (id && currentUserId) markChatRead(currentUserId, conversationKey('task', id));
  }, [id, currentUserId, markChatRead]);

  // Focus handling from URL ?focus=claim|complete|reassign
  useEffect(() => {
    if (!task || !focus) return;
    if (focus === 'reassign') {
      setShowReassign(true);
    } else if (focus === 'claim' && claimButtonRef.current) {
      claimButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedAction('claim');
    } else if (focus === 'complete' && completeButtonRef.current) {
      completeButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedAction('complete');
    }
  }, [focus, task]);

  const refreshTask = async () => {
    try {
      const res = await api.get(`/tasks/${id}`);
      const data = (res.data as any)?.data;
      if (data) setTask(data);
    } catch {
      // silent
    }
  };

  // ---- Action Handlers ----

  const handleClaim = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      await api.put(`/tasks/${id}/claim`);
      await refreshTask();
    } catch (err: any) {
      setActionError(getApiErrorMessage(err, 'Failed to claim task.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveAsTemplate = async () => {
    setTemplateLoading(true);
    setTemplateError(null);
    try {
      await api.post(`/tasks/${id}/save-as-template`, { name: templateName.trim() || undefined });
      setTemplateSuccess(true);
      setShowTemplateForm(false);
      setTemplateName('');
    } catch (err: any) {
      setTemplateError(getApiErrorMessage(err, t('tasks.detail.template.error')));
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!completeNote.trim() || completeNote.trim().length < 2) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await api.put(`/tasks/${id}/complete`, { note: completeNote.trim() });
      await refreshTask();
      setShowCompleteForm(false);
      setCompleteNote('');
    } catch (err: any) {
      setActionError(getApiErrorMessage(err, 'Failed to complete task.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReassign = async () => {
    if (!reassignId || !reassignNote.trim()) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const body = reassignType === 'user'
        ? { assigned_to_user_id: reassignId, note: reassignNote.trim() }
        : { assigned_to_group_id: reassignId, note: reassignNote.trim() };
      await api.put(`/tasks/${id}/reassign`, body);
      await refreshTask();
      await fetchMessages();
      setShowReassign(false);
      setReassignId('');
      setReassignNote('');
    } catch (err: any) {
      setActionError(getApiErrorMessage(err, 'Failed to reassign task.'));
    } finally {
      setActionLoading(false);
    }
  };

  // ---- Note Handler ----

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = noteText.trim();
    if (!text) return;

    setNoteLoading(true);
    setNoteError(null);
    try {
      await api.post(`/tasks/${id}/notes`, { note: text });
      setNoteText('');
      await refreshTask();
    } catch (err: any) {
      setNoteError(getApiErrorMessage(err, 'Failed to add note.'));
    } finally {
      setNoteLoading(false);
    }
  };

  // ---- Watcher Handlers ----

  const handleAddWatcher = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!watcherId) return;

    setWatcherLoading(true);
    setWatcherError(null);
    try {
      const body = watcherType === 'user'
        ? { user_id: watcherId }
        : { group_id: watcherId };
      await api.post(`/tasks/${id}/watchers`, body);
      setWatcherType('user');
      setWatcherId('');
      await refreshTask();
    } catch (err: any) {
      setWatcherError(getApiErrorMessage(err, 'Failed to add watcher.'));
    } finally {
      setWatcherLoading(false);
    }
  };

  const handleRemoveWatcher = async (watcherId: string) => {
    setWatcherRemovingId(watcherId);
    setWatcherError(null);
    try {
      await api.delete(`/tasks/${id}/watchers/${watcherId}`);
      await refreshTask();
    } catch (err: any) {
      setWatcherError(getApiErrorMessage(err, 'Failed to remove watcher.'));
    } finally {
      setWatcherRemovingId(null);
    }
  };

  // ---- Chat Handler ----

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 8 * 1024 * 1024; // 8MB
    if (file.size > maxSize) {
      setMessageError(t('tasks.detail.chat.imageTooLarge'));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
    setMessageError(null);
  };

  const handleRemoveImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ---- @Mention autocomplete ----

  const mentionCandidates = useMemo(() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.toLowerCase();
    return mentionableMembers
      .filter((m) => m.id !== currentUser?.id && m.name?.trim())
      .filter((m) => m.name.toLowerCase().includes(q))
      .slice(0, 6);
  }, [mentionQuery, mentionableMembers, currentUser]);

  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMessageText(value);

    const caret = e.target.selectionStart ?? value.length;
    let atIndex = -1;
    for (let i = caret - 1; i >= 0 && caret - i <= 30; i--) {
      const char = value[i];
      if (char === '@') {
        const before = i === 0 ? ' ' : value[i - 1];
        if (/\s/.test(before)) atIndex = i;
        break;
      }
      if (char === '\n') break;
    }

    if (atIndex === -1) {
      setMentionQuery(null);
      setMentionStart(-1);
      setMentionEnd(-1);
      return;
    }
    setMentionStart(atIndex);
    setMentionEnd(caret);
    setMentionQuery(value.slice(atIndex + 1, caret));
    setMentionIndex(0);
  };

  const insertMention = (member: { id: string; name: string; email: string }) => {
    const before = messageText.slice(0, mentionStart);
    const after = messageText.slice(mentionEnd);
    const inserted = `@${member.name.trim()} `;
    const nextText = before + inserted + after;
    setMessageText(nextText);
    setMentionQuery(null);
    setMentionStart(-1);
    setMentionEnd(-1);

    const pos = before.length + inserted.length;
    requestAnimationFrame(() => {
      messageInputRef.current?.focus();
      messageInputRef.current?.setSelectionRange(pos, pos);
    });
  };

  const handleMessageKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (mentionQuery === null || mentionCandidates.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionIndex((i) => (i + 1) % mentionCandidates.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionIndex((i) => (i - 1 + mentionCandidates.length) % mentionCandidates.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      // Must preventDefault BEFORE inserting — otherwise Enter also
      // submits the enclosing form and sends a half-typed message.
      e.preventDefault();
      insertMention(mentionCandidates[mentionIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setMentionQuery(null);
      setMentionStart(-1);
      setMentionEnd(-1);
    }
  };

  // Re-derives mentioned user ids from the FINAL message text at send
  // time (rather than tracking a separate "selected ids" array) so that
  // manually deleting an inserted "@Name" correctly drops the mention —
  // no separate state to fall out of sync with what's on screen. Longest
  // names are matched first and consumed from a scratch copy of the text
  // so "@Ali Veli" can't also register as a match for a shorter "Ali".
  const extractMentionedUserIds = (text: string): string[] => {
    const ids: string[] = [];
    let scratch = text;
    const byLongestName = [...members]
      .filter((m) => m.id !== currentUser?.id && m.name?.trim())
      .sort((a, b) => b.name.trim().length - a.name.trim().length);

    for (const m of byLongestName) {
      const token = `@${m.name.trim()}`;
      if (scratch.includes(token)) {
        ids.push(m.id);
        scratch = scratch.split(token).join(' ');
      }
    }
    return Array.from(new Set(ids));
  };

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    const hasText = messageText.trim().length > 0;
    const hasImage = selectedImage !== null;
    if (!hasText && !hasImage) return;

    setMentionQuery(null);
    // Computed once, before the try, so both the initial send and the
    // quota-eviction retry below share the exact same mention list.
    const mentionedIds = hasText ? extractMentionedUserIds(messageText.trim()) : [];

    setSendingMessage(true);
    setMessageError(null);
    try {
      const formData = new FormData();
      if (hasText) formData.append('message', messageText.trim());
      if (hasImage) formData.append('image', selectedImage);
      if (mentionedIds.length) formData.append('mentioned_user_ids', JSON.stringify(mentionedIds));

      await api.post(`/tasks/${id}/messages`, formData);
      setMessageText('');
      handleRemoveImage();
      await fetchMessages();
    } catch (err: any) {
      if (axios.isAxiosError(err) && err.response?.status === 409 && err.response.data?.error?.code === 'QUOTA_EXCEEDED') {
        const { candidates, neededBytes } = err.response.data.data;
        const names = candidates.map((c: any) => c.original_name).join(', ');
        const confirmMsg = t('fileQuota.confirmPrompt', { count: candidates.length, names, size: formatSize(neededBytes) });
        if (await confirm(confirmMsg)) {
          try {
            const retryFormData = new FormData();
            if (hasText) retryFormData.append('message', messageText.trim());
            if (hasImage) retryFormData.append('image', selectedImage as File);
            if (mentionedIds.length) retryFormData.append('mentioned_user_ids', JSON.stringify(mentionedIds));
            retryFormData.append('confirmEviction', 'true');
            await api.post(`/tasks/${id}/messages`, retryFormData);
            setMessageText('');
            handleRemoveImage();
            await fetchMessages();
          } catch (retryErr: any) {
            setMessageError(getApiErrorMessage(retryErr, 'Failed to send message.'));
          }
        }
      } else {
        setMessageError(getApiErrorMessage(err, 'Failed to send message.'));
      }
    } finally {
      setSendingMessage(false);
    }
  };

  // ---- Helpers ----

  const getStatusBadge = (status: string) => {
    const config: Record<string, { color: string; label: string }> = {
      todo: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300', label: t('tasks.detail.status.todo') },
      claimed: { color: 'bg-yellow-100 text-yellow-800 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-1 dark:ring-inset dark:ring-amber-500/30', label: t('tasks.detail.status.claimed') },
      completed: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', label: t('tasks.detail.status.completed') }
    };
    const c = config[status] || { color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300', label: status };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${c.color}`}>
        {c.label}
      </span>
    );
  };

  const getAssigneeName = (): string => {
    if (!task) return '-';
    if (task.assigned_to_user_id) {
      const m = members.find((m) => m.id === task.assigned_to_user_id);
      return m ? m.name : task.assigned_to_user_id;
    }
    if (task.assigned_to_group_id) {
      const g = groups.find((g) => g.id === task.assigned_to_group_id);
      return g ? g.name : task.assigned_to_group_id;
    }
    return '-';
  };

  const getCreatorName = (): string => {
    if (!task) return '-';
    const m = members.find((m) => m.id === task.created_by);
    return m ? m.name : task.created_by;
  };

  const canActOnTask = (): boolean => {
    if (!currentUser || !task) return false;
    if (task.assigned_to_user_id === currentUser.id) return true;
    if (task.assigned_to_group_id && (currentUser.group_ids ?? (currentUser.group_id ? [currentUser.group_id] : [])).includes(task.assigned_to_group_id)) return true;
    return false;
  };

  // Backend's reassignTask lets manager/superadmin reassign regardless of
  // assignee (TaskService.isPrivilegedRole) — UI visibility must match.
  // NOTE: complete stays assignee-only; this must NOT gate the Complete button.
  const isPrivilegedUser = currentUser?.role === 'superadmin' || currentUser?.role === 'manager';

  // ---- Task Edit (title/description) Handlers ----
  // Backend: PUT /api/tasks/:id body {title?, description?}; allowed for the
  // task creator (created_by) or manager/superadmin (403 otherwise).
  const canEditTask =
    !!currentUser && !!task && (task.created_by === currentUser.id || isPrivilegedUser);

  const handleStartEditTask = () => {
    if (!task) return;
    setEditTitle(task.title);
    setEditDescription(task.description);
    setEditError(null);
    setIsEditingTask(true);
  };

  const handleCancelEditTask = () => {
    setIsEditingTask(false);
    setEditError(null);
  };

  const handleSaveEditTask = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditSaving(true);
    setEditError(null);
    try {
      await api.put(`/tasks/${id}`, {
        title: editTitle,
        description: editDescription
      });
      await refreshTask();
      setIsEditingTask(false);
    } catch (err: any) {
      setEditError(getApiErrorMessage(err, t('errors.general')));
    } finally {
      setEditSaving(false);
    }
  };

  // ---- Message Edit/Delete Handlers ----
  // Mirrors the incident comment edit/delete pattern in IncidentDetails.tsx.
  // Backend: PATCH and DELETE /tasks/:id/messages/:messageId both allow
  // author OR manager/superadmin. Only the text part is editable — the
  // image attachment (msg.attachment) stays untouched.
  const canEditMessage = (msg: TaskMessage): boolean =>
    !!currentUser &&
    (msg.author_id === currentUser.id || currentUser.role === 'superadmin' || currentUser.role === 'manager');

  const canDeleteMessage = (msg: TaskMessage): boolean =>
    !!currentUser &&
    (msg.author_id === currentUser.id || currentUser.role === 'superadmin' || currentUser.role === 'manager');

  const handleStartEditMessage = (msg: TaskMessage) => {
    setEditingMessageId(msg.id);
    setEditingMessageText(msg.message || '');
    setMessageError(null);
  };

  const handleCancelEditMessage = () => {
    setEditingMessageId(null);
    setEditingMessageText('');
  };

  const handleSaveEditMessage = async (messageId: string) => {
    if (!editingMessageText.trim()) return;
    setMessageActionLoading(messageId);
    setMessageError(null);
    try {
      const res = await api.patch(`/tasks/${id}/messages/${messageId}`, { message: editingMessageText.trim() });
      const updated = (res.data as any)?.data;
      if (updated) {
        setMessages((prev) => prev.map((m) => (m.id === messageId ? updated : m)));
      } else {
        await fetchMessages();
      }
      setEditingMessageId(null);
      setEditingMessageText('');
    } catch (err: any) {
      setMessageError(getApiErrorMessage(err, t('errors.general')));
    } finally {
      setMessageActionLoading(null);
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (!(await confirm(t('tasks.detail.chat.deleteConfirm'), { variant: 'danger' }))) return;
    setMessageActionLoading(messageId);
    setMessageError(null);
    try {
      await api.delete(`/tasks/${id}/messages/${messageId}`);
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
    } catch (err: any) {
      setMessageError(getApiErrorMessage(err, t('errors.general')));
    } finally {
      setMessageActionLoading(null);
    }
  };

  // Claim is the one action the task's creator can also take (mirrors
  // TaskService.claimTask on the backend) — complete/reassign stay
  // assignee-only, so this must NOT be folded into canActOnTask above.
  const canClaimTask = (): boolean => {
    if (!currentUser || !task) return false;
    if (task.created_by === currentUser.id) return true;
    return canActOnTask();
  };

  const canAddNote = (): boolean => {
    if (!currentUser || !task) return false;
    if (canActOnTask()) return true;
    if (task.created_by === currentUser.id) return true;
    if (currentUser.role === 'superadmin' || currentUser.role === 'manager') return true;
    return false;
  };

  const canAddWatcher = (): boolean => {
    if (!currentUser || !task) return false;
    if (canActOnTask()) return true;
    if (task.created_by === currentUser.id) return true;
    if (currentUser.role === 'superadmin' || currentUser.role === 'manager') return true;
    return false;
  };

  const getWatcherName = (w: NonNullable<Task['watchers']>[number]): string => {
    if (w.watcher_type === 'user') {
      const m = members.find((m) => m.id === w.user_id);
      return m ? m.name : (w.user_id || '-');
    }
    const g = groups.find((g) => g.id === w.group_id);
    return g ? g.name : (w.group_id || '-');
  };

  const getAuthorName = (authorId: string): string => {
    if (authorId === currentUser?.id) return t('tasks.detail.chat.you');
    const m = members.find((m) => m.id === authorId);
    return m ? m.name : authorId;
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString(resolveLocale(language), {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
      return dateStr;
    }
  };

  // ---- Render ----

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <span className="ml-3 text-gray-600 dark:text-gray-400">{t('tasks.detail.loading')}</span>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="space-y-6">
        <div>
          <Link to="/tasks" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm">&larr; {t('tasks.detail.backToList')}</Link>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('tasks.detail.notFound')}</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{t('tasks.detail.notFoundDesc')}</p>
          <Link to="/tasks" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline">{t('tasks.detail.backToList')}</Link>
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="space-y-6">
        <div>
          <Link to="/tasks" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm">&larr; {t('tasks.detail.backToList')}</Link>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('tasks.detail.forbiddenTitle')}</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{t('tasks.detail.forbiddenDesc')}</p>
          <Link to="/tasks" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline">{t('tasks.detail.backToList')}</Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <Link to="/tasks" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm">&larr; {t('tasks.detail.backToList')}</Link>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-900/40 dark:text-yellow-400 px-4 py-3 rounded-lg">{error}</div>
      </div>
    );
  }

  if (!task) return null;

  return (
    <div className="space-y-4">
      {/* Back link */}
      <div>
        <Link to="/tasks" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm">&larr; {t('tasks.detail.backToList')}</Link>
      </div>

      {/* Header: key, title, status, meta, and actions — one compact card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                <span className="text-gray-400 dark:text-gray-500 font-normal">{task.project_key}-{task.task_number}</span>
                <span className="mx-2 text-gray-300 dark:text-gray-600">·</span>
                {task.title}
              </h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                <span>{t('tasks.detail.fields.createdBy')}: <span className="text-gray-700 dark:text-gray-300 font-medium">{getCreatorName()}</span></span>
                <span>{t('tasks.detail.fields.assignedTo')}: <span className="text-gray-700 dark:text-gray-300 font-medium">{getAssigneeName()}</span></span>
                <span>{t('tasks.detail.fields.createdAt')}: {formatDate(task.created_at)}</span>
                {task.claimed_at && <span>{t('tasks.detail.fields.claimedAt')}: {formatDate(task.claimed_at)}</span>}
                {task.completed_at && <span>{t('tasks.detail.fields.completedAt')}: {formatDate(task.completed_at)}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge(task.status)}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-5 py-4">
          {actionError && (
            <div className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-900/40 dark:text-red-400 px-4 py-3 rounded-lg text-sm mb-4">{actionError}</div>
          )}

          {task.status === 'todo' && canClaimTask() && (
            <button
              ref={claimButtonRef}
              onClick={handleClaim}
              disabled={actionLoading}
              className={`px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 ${highlightedAction === 'claim' ? 'ring-2 ring-blue-400 ring-offset-2' : ''}`}
            >
              {actionLoading ? t('tasks.detail.actions.processing') : t('tasks.detail.actions.claim')}
            </button>
          )}

          {task.status === 'claimed' && (canActOnTask() || isPrivilegedUser) && (
            <div className="flex flex-wrap items-center gap-3">
              {canActOnTask() && (
                <button
                  ref={completeButtonRef}
                  onClick={() => { setShowCompleteForm(true); setActionError(null); }}
                  disabled={actionLoading}
                  className={`px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 ${highlightedAction === 'complete' ? 'ring-2 ring-blue-400 ring-offset-2' : ''}`}
                >
                  {actionLoading ? t('tasks.detail.actions.processing') : t('tasks.detail.actions.complete')}
                </button>
              )}
              <button
                onClick={() => { setShowReassign((prev) => !prev); setActionError(null); }}
                disabled={actionLoading}
                className="px-5 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                {t('tasks.detail.actions.reassign')}
              </button>
            </div>
          )}

          {task.status === 'claimed' && canActOnTask() && showCompleteForm && (
            <form onSubmit={(e) => { e.preventDefault(); handleComplete(); }} className="mt-4 p-4 border border-green-200 dark:border-green-900/40 rounded-lg bg-green-50 dark:bg-green-900/20 space-y-4 max-w-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('tasks.detail.actions.completionNote')}</label>
                <textarea
                  required
                  minLength={2}
                  rows={3}
                  value={completeNote}
                  onChange={(e) => setCompleteNote(e.target.value)}
                  placeholder={t('tasks.detail.actions.completionNotePlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={actionLoading || !completeNote.trim() || completeNote.trim().length < 2}
                  className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? t('tasks.detail.actions.processing') : t('tasks.detail.actions.complete')}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowCompleteForm(false); setCompleteNote(''); setActionError(null); }}
                  disabled={actionLoading}
                  className="px-5 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          )}

          {task.status === 'claimed' && (canActOnTask() || isPrivilegedUser) && showReassign && (
            <div className="mt-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/40 space-y-4">
              <div className="flex gap-4">
                <label className="flex items-center text-sm">
                  <input
                    type="radio"
                    name="reassignType"
                    value="user"
                    checked={reassignType === 'user'}
                    onChange={() => { setReassignType('user'); setReassignId(''); }}
                    className="mr-1"
                  />
                  {t('tasks.detail.actions.reassignToUser')}
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="radio"
                    name="reassignType"
                    value="group"
                    checked={reassignType === 'group'}
                    onChange={() => { setReassignType('group'); setReassignId(''); }}
                    className="mr-1"
                  />
                  {t('tasks.detail.actions.reassignToGroup')}
                </label>
              </div>

              {reassignType === 'user' ? (
                <select
                  value={reassignId}
                  onChange={(e) => setReassignId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('tasks.detail.actions.selectUser')}</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>{m.name} ({m.role})</option>
                  ))}
                </select>
              ) : (
                <select
                  value={reassignId}
                  onChange={(e) => setReassignId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('tasks.detail.actions.selectGroup')}</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('tasks.detail.actions.reassignNote')} *</label>
                <textarea
                  required
                  minLength={2}
                  rows={2}
                  value={reassignNote}
                  onChange={(e) => setReassignNote(e.target.value)}
                  placeholder={t('tasks.detail.actions.reassignNotePlaceholder') as string}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleReassign}
                  disabled={actionLoading || !reassignId || !reassignNote.trim()}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? t('tasks.detail.actions.processing') : t('tasks.detail.actions.confirmReassign')}
                </button>
                <button
                  onClick={() => { setShowReassign(false); setReassignId(''); setReassignNote(''); setActionError(null); }}
                  disabled={actionLoading}
                  className="px-5 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}

          {task.status === 'completed' && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('tasks.detail.info.completed')}</p>
          )}

          {task.status === 'todo' && !canClaimTask() && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('tasks.detail.info.notAssigned')}</p>
          )}

          {task.status === 'claimed' && !(canActOnTask() || isPrivilegedUser) && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('tasks.detail.info.takenByOther')}</p>
          )}

          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            {templateError && (
              <div className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-900/40 dark:text-red-400 px-4 py-3 rounded-lg text-sm mb-3">{templateError}</div>
            )}
            {templateSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-900/40 dark:text-green-400 px-4 py-3 rounded-lg text-sm mb-3">{t('tasks.detail.template.success')}</div>
            )}

            {!showTemplateForm ? (
              <button
                type="button"
                onClick={() => { setShowTemplateForm(true); setTemplateName(task.title); setTemplateSuccess(false); }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
              >
                {t('tasks.detail.template.saveAsTemplate')}
              </button>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder={t('tasks.detail.template.namePlaceholder')}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={handleSaveAsTemplate}
                  disabled={templateLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm"
                >
                  {templateLoading ? t('tasks.detail.actions.processing') : t('common.save')}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowTemplateForm(false); setTemplateError(null); }}
                  disabled={templateLoading}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 text-sm"
                >
                  {t('common.cancel')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Two-column body: main content (description/notes/chat) + compact sidebar (attachments/watchers) */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4 items-start">
        <div className="space-y-4 min-w-0">
          {/* Description */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="px-5 py-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('common.description')}</h2>
                {canEditTask && !isEditingTask && (
                  <button
                    onClick={handleStartEditTask}
                    className="p-1 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                    title={t('common.edit') as string}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                )}
              </div>
              {isEditingTask ? (
                <form onSubmit={handleSaveEditTask} className="space-y-4">
                  {editError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-900/40 dark:text-red-400 px-4 py-3 rounded-lg text-sm">{editError}</div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('common.title')} *</label>
                    <input
                      required
                      minLength={2}
                      maxLength={255}
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('common.description')} *</label>
                    <textarea
                      required
                      minLength={2}
                      rows={4}
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={editSaving}
                      className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {editSaving ? t('tasks.detail.actions.processing') : t('common.save')}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEditTask}
                      className="px-5 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </form>
              ) : (
                <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{task.description}</p>
              )}
            </div>
          </div>

          {/* Notes section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="px-5 py-4">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('tasks.detail.notes.title')}</h2>

              {noteError && (
                <div className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-900/40 dark:text-red-400 px-4 py-3 rounded-lg text-sm mb-4">{noteError}</div>
              )}

              {task.notes && task.notes.length > 0 ? (
                <div className="space-y-3">
                  {task.notes.map((note) => (
                    <div key={note.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{getAuthorName(note.author_id)}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(note.created_at)}</span>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{note.note}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('tasks.detail.notes.noNotes')}</p>
              )}

              {canAddNote() && (
                <form onSubmit={handleAddNote} className="mt-3 space-y-3">
                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder={t('tasks.detail.notes.placeholder')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={noteLoading}
                  />
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={noteLoading || !noteText.trim()}
                      className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {noteLoading ? t('tasks.detail.actions.processing') : t('tasks.detail.notes.add')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Chat section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('tasks.detail.chat.title')}</h2>
            </div>

            {/* Messages */}
            <div className="h-80 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900/40">
              {messagesLoading && messages.length === 0 && (
                <div className="flex items-center justify-center py-8">
                  <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="ml-2 text-gray-500 dark:text-gray-400 text-sm">{t('tasks.detail.loading')}</span>
                </div>
              )}

              {!messagesLoading && messages.length === 0 && (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8 text-sm">{t('tasks.detail.chat.noMessages')}</p>
              )}

              {messages.length > 0 && (
                <div className="space-y-3">
                  {messages.map((msg) => {
                    const isMine = msg.author_id === currentUser?.id;
                    return (
                      <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                        <div className={`group max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          isMine ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        }`}>
                          {msg.message_type === 'reassign' && (
                            <span className={`inline-block text-xs font-medium rounded px-1.5 py-0.5 mb-1 ${isMine ? 'bg-blue-400 text-white' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400'}`}>
                              {t('tasks.detail.chat.reassignBadge')}
                            </span>
                          )}
                          {editingMessageId === msg.id ? (
                            <div className="space-y-2">
                              <textarea
                                rows={2}
                                value={editingMessageText}
                                onChange={(e) => setEditingMessageText(e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleSaveEditMessage(msg.id)}
                                  disabled={messageActionLoading === msg.id || !editingMessageText.trim()}
                                  className={`px-3 py-1 rounded text-xs transition-colors disabled:opacity-50 ${isMine ? 'bg-white text-blue-600 hover:bg-blue-50' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                                >
                                  {t('tasks.detail.chat.save')}
                                </button>
                                <button
                                  onClick={handleCancelEditMessage}
                                  disabled={messageActionLoading === msg.id}
                                  className={`px-3 py-1 border rounded text-xs transition-colors disabled:opacity-50 ${isMine ? 'border-blue-200 text-blue-100 hover:bg-blue-600' : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                                >
                                  {t('tasks.detail.chat.cancel')}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {msg.message && <p className="text-sm">{msg.message}</p>}
                              {msg.attachment && (
                                <div className="mt-1">
                                  <AuthenticatedImage
                                    src={`/tasks/${task.id}/messages/attachments/${msg.attachment.id}/download`}
                                    alt={msg.attachment.original_name || 'image'}
                                    className="max-w-full max-h-[200px] h-auto rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                    clickToOpen
                                  />
                                </div>
                              )}
                            </>
                          )}
                          <div className={`flex items-center gap-1 mt-1 ${isMine ? 'justify-end' : 'justify-start'}`}>
                            <p className={`text-xs whitespace-nowrap opacity-75 ${isMine ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'}`}>
                              {!isMine && `${getAuthorName(msg.author_id)} · `}{formatTime(msg.created_at)}{msg.updated_at ? ` ${t('tasks.detail.chat.edited')}` : ''}
                            </p>
                            {!editingMessageId && canEditMessage(msg) && (
                              <button
                                onClick={() => handleStartEditMessage(msg)}
                                disabled={messageActionLoading === msg.id}
                                className={`p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50 ${isMine ? 'text-blue-100 hover:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'}`}
                                title={t('tasks.detail.chat.edit') as string}
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                            )}
                            {!editingMessageId && canDeleteMessage(msg) && (
                              <button
                                onClick={() => handleDeleteMessage(msg.id)}
                                disabled={messageActionLoading === msg.id}
                                className={`p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50 ${isMine ? 'text-blue-100 hover:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400'}`}
                                title={t('tasks.detail.chat.delete') as string}
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={chatEndRef} />
                </div>
              )}
            </div>

            {/* Message input */}
            <div className="border-t border-gray-200 dark:border-gray-700">
              {messageError && (
                <div className="bg-red-50 border-b border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-900/40 dark:text-red-400 px-4 py-2 text-sm">{messageError}</div>
              )}

              {/* Image preview */}
              {imagePreview && (
                <div className="px-4 pt-3 pb-1 flex items-center gap-2">
                  <div className="relative inline-block">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="h-16 w-auto rounded-lg border border-gray-300 dark:border-gray-600"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                      title={t('tasks.detail.chat.removeImage')}
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}

              <form onSubmit={handleSendMessage} className="flex gap-2 p-4">
                <div className="relative flex-1">
                  <input
                    ref={messageInputRef}
                    type="text"
                    value={messageText}
                    onChange={handleMessageChange}
                    onKeyDown={handleMessageKeyDown}
                    placeholder={t('tasks.detail.chat.placeholder')}
                    title={t('tasks.detail.chat.mentions.hint') as string}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={sendingMessage}
                    role="combobox"
                    aria-autocomplete="list"
                    aria-controls="mention-suggestions"
                    aria-expanded={mentionQuery !== null && mentionCandidates.length > 0}
                  />
                  {mentionQuery !== null && mentionCandidates.length > 0 && (
                    <ul
                      id="mention-suggestions"
                      role="listbox"
                      aria-label={t('tasks.detail.chat.mentions.listLabel') as string}
                      className="absolute bottom-full mb-1 left-0 w-full max-h-48 overflow-y-auto z-20 rounded-lg border shadow-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                    >
                      {mentionCandidates.map((m, i) => (
                        <li key={m.id} role="option" aria-selected={i === mentionIndex}>
                          <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onMouseEnter={() => setMentionIndex(i)}
                            onClick={() => insertMention(m)}
                            className={`w-full text-left px-3 py-2 text-sm ${
                              i === mentionIndex ? 'bg-blue-50 dark:bg-gray-700' : ''
                            }`}
                          >
                            <span className="font-medium text-gray-900 dark:text-gray-100">{m.name}</span>{' '}
                            <span className="text-xs text-gray-500 dark:text-gray-400">{m.email}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sendingMessage}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                  title={t('tasks.detail.chat.addImage')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>
                <button
                  type="submit"
                  disabled={sendingMessage || (!messageText.trim() && !selectedImage)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
                >
                  {sendingMessage ? (
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Sidebar: attachments + watchers */}
        <div className="space-y-4">
          {task.attachments && task.attachments.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
              <div className="px-4 py-4">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">{t('tasks.detail.attachments')} · {task.attachments.length}</h3>
                <div className="grid grid-cols-3 gap-2">
                  {task.attachments.map((att) => (
                    <AuthenticatedImage
                      key={att.id}
                      src={`/tasks/${task.id}/attachments/${att.id}/download`}
                      alt={att.original_name || 'attachment'}
                      className="aspect-square w-full object-cover rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:opacity-90 transition-opacity"
                      clickToOpen
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Watchers section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="px-4 py-4">
              <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">{t('tasks.detail.watchers.title')}</h2>

              {watcherError && (
                <div className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-900/40 dark:text-red-400 px-3 py-2 rounded-lg text-sm mb-3">{watcherError}</div>
              )}

              {task.watchers && task.watchers.length > 0 ? (
                <div className="space-y-2">
                  {task.watchers.map((w) => (
                    <div key={w.id} className="flex items-center justify-between border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5">
                      <span className="text-sm text-gray-900 dark:text-gray-100 truncate">{getWatcherName(w)}</span>
                      {(task.created_by === currentUser?.id || currentUser?.role === 'superadmin' || currentUser?.role === 'manager') && (
                        <button
                          type="button"
                          onClick={() => handleRemoveWatcher(w.id)}
                          disabled={watcherRemovingId === w.id}
                          className="ml-2 flex-shrink-0 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 rounded hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 hover:border-red-200 dark:hover:border-red-900/40 transition-colors disabled:opacity-50"
                        >
                          {watcherRemovingId === w.id ? t('tasks.detail.actions.processing') : t('tasks.detail.watchers.remove')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('tasks.detail.watchers.noWatchers')}</p>
              )}

              {canAddWatcher() && (
                <form onSubmit={handleAddWatcher} className="mt-3 border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-900/40 space-y-3">
                  <div className="flex gap-4">
                    <label className="flex items-center text-sm">
                      <input
                        type="radio"
                        name="watcherType"
                        value="user"
                        checked={watcherType === 'user'}
                        onChange={() => { setWatcherType('user'); setWatcherId(''); }}
                        className="mr-1"
                      />
                      {t('tasks.detail.watchers.user')}
                    </label>
                    <label className="flex items-center text-sm">
                      <input
                        type="radio"
                        name="watcherType"
                        value="group"
                        checked={watcherType === 'group'}
                        onChange={() => { setWatcherType('group'); setWatcherId(''); }}
                        className="mr-1"
                      />
                      {t('tasks.detail.watchers.group')}
                    </label>
                  </div>

                  {watcherType === 'user' ? (
                    <select
                      value={watcherId}
                      onChange={(e) => setWatcherId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">{t('tasks.detail.watchers.selectUser')}</option>
                      {members.map((m) => (
                        <option key={m.id} value={m.id}>{m.name} ({m.role})</option>
                      ))}
                    </select>
                  ) : (
                    <select
                      value={watcherId}
                      onChange={(e) => setWatcherId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">{t('tasks.detail.watchers.selectGroup')}</option>
                      {groups.map((g) => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  )}

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={watcherLoading || !watcherId}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm"
                    >
                      {watcherLoading ? t('tasks.detail.actions.processing') : t('tasks.detail.watchers.add')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDetails;
